/**
 * Treatment Record service for managing therapy treatment records
 */
import StorageService from './StorageService';
import TreatmentRecord from '../models/TreatmentRecord';
import customerService from './CustomerService';
import practitionerService from './PractitionerService';
import treatmentService from './TreatmentService';

class TreatmentRecordService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('treatment_records');
  }

  /**
   * Get all treatment records
   * @returns {Array} All treatment records
   */
  getAllTreatmentRecords() {
    return this.storageService.getAllAsArray().map(record => 
      TreatmentRecord.fromJSON(record)
    );
  }

  /**
   * Get treatment record by ID
   * @param {string} id - Treatment record ID
   * @returns {TreatmentRecord|null} Treatment record or null if not found
   */
  getTreatmentRecordById(id) {
    const recordData = this.storageService.getById(id);
    if (!recordData) return null;
    
    return TreatmentRecord.fromJSON(recordData);
  }

  /**
   * Create treatment record
   * @param {Object} recordData - Treatment record data
   * @returns {TreatmentRecord} Created treatment record
   */
  createTreatmentRecord(recordData) {
    const record = new TreatmentRecord(recordData);
    this.storageService.save(record.id, record.toJSON());
    return record;
  }

  /**
   * Update treatment record
   * @param {string} id - Treatment record ID
   * @param {Object} recordData - Treatment record data to update
   * @returns {TreatmentRecord|null} Updated treatment record or null if not found
   */
  updateTreatmentRecord(id, recordData) {
    const existingRecordData = this.storageService.getById(id);
    if (!existingRecordData) return null;
    
    const record = TreatmentRecord.fromJSON(existingRecordData);
    record.update(recordData);
    
    this.storageService.save(record.id, record.toJSON());
    return record;
  }

  /**
   * Delete treatment record
   * @param {string} id - Treatment record ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteTreatmentRecord(id) {
    return this.storageService.delete(id);
  }

  /**
   * Get treatment records by customer
   * @param {string} customerId - Customer ID
   * @returns {Array} Customer's treatment records
   */
  getTreatmentRecordsByCustomer(customerId) {
    return this.storageService.find(record => 
      record.customerId === customerId
    ).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Get treatment records by practitioner
   * @param {string} practitionerId - Practitioner ID
   * @returns {Array} Practitioner's treatment records
   */
  getTreatmentRecordsByPractitioner(practitionerId) {
    return this.storageService.find(record => 
      record.practitionerId === practitionerId
    ).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Get treatment records by treatment type
   * @param {string} treatmentId - Treatment ID
   * @returns {Array} Records for specified treatment
   */
  getTreatmentRecordsByTreatment(treatmentId) {
    return this.storageService.find(record => 
      record.treatmentId === treatmentId
    ).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Get treatment records by date range
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @returns {Array} Treatment records in date range
   */
  getTreatmentRecordsByDateRange(startDate, endDate) {
    const start = startDate instanceof Date ? startDate : new Date(startDate);
    const end = endDate instanceof Date ? endDate : new Date(endDate);
    
    start.setHours(0, 0, 0, 0); // Beginning of start day
    end.setHours(23, 59, 59, 999); // End of end day
    
    return this.storageService.find(record => {
      const recordDate = new Date(record.date);
      return recordDate >= start && recordDate <= end;
    }).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Get treatment records associated with an appointment
   * @param {string} appointmentId - Appointment ID
   * @returns {Array} Treatment records for appointment
   */
  getTreatmentRecordsByAppointment(appointmentId) {
    return this.storageService.find(record => 
      record.appointmentId === appointmentId
    ).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Search treatment records by text query
   * @param {string} query - Search query
   * @returns {Array} Matching treatment records
   */
  searchTreatmentRecords(query) {
    if (!query) return this.getAllTreatmentRecords();
    
    const lowercasedQuery = query.toLowerCase();
    
    return this.storageService.find(record => {
      // Search in various fields
      return (
        record.chiefComplaint?.toLowerCase().includes(lowercasedQuery) ||
        record.diagnosis?.tcm?.toLowerCase().includes(lowercasedQuery) ||
        record.diagnosis?.western?.toLowerCase().includes(lowercasedQuery) ||
        record.diagnosis?.pattern?.toLowerCase().includes(lowercasedQuery) ||
        record.notes?.toLowerCase().includes(lowercasedQuery)
      );
    }).map(record => TreatmentRecord.fromJSON(record));
  }

  /**
   * Add a treatment point to record
   * @param {string} id - Treatment record ID
   * @param {string} point - Treatment point name
   * @param {string} [description=''] - Description or technique used
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  addTreatmentPoint(id, point, description = '') {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.addTreatmentPoint(point, description);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Add a prescription to record
   * @param {string} id - Treatment record ID
   * @param {string} name - Prescription name
   * @param {string} [dosage=''] - Dosage information
   * @param {string} [instructions=''] - Usage instructions
   * @param {number} [quantity=0] - Quantity provided
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  addPrescription(id, name, dosage = '', instructions = '', quantity = 0) {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.addPrescription(name, dosage, instructions, quantity);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Set treatment outcome
   * @param {string} id - Treatment record ID
   * @param {Object} outcome - Treatment outcome data
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  setOutcome(id, outcome) {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.setOutcome(outcome);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Add home care instruction to record
   * @param {string} id - Treatment record ID
   * @param {string} instruction - Instruction text
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  addHomeInstruction(id, instruction) {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.addHomeInstruction(instruction);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Set TCM observation data
   * @param {string} id - Treatment record ID
   * @param {Object} observations - TCM observation data
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  setTCMObservations(id, observations) {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.setTCMObservations(observations);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Add an attachment to record
   * @param {string} id - Treatment record ID
   * @param {string} fileName - File name
   * @param {string} fileUrl - URL or path to file
   * @param {string} [fileType='image'] - Type of file
   * @param {string} [description=''] - Description of the attachment
   * @returns {TreatmentRecord|null} Updated record or null if not found
   */
  addAttachment(id, fileName, fileUrl, fileType = 'image', description = '') {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    record.addAttachment(fileName, fileUrl, fileType, description);
    this.storageService.save(record.id, record.toJSON());
    
    return record;
  }

  /**
   * Get treatment record with related details
   * @param {string} id - Treatment record ID
   * @returns {Object|null} Treatment record with related details
   */
  getTreatmentRecordWithDetails(id) {
    const record = this.getTreatmentRecordById(id);
    if (!record) return null;
    
    // Get related data
    const customer = customerService.getCustomerById(record.customerId);
    const practitioner = practitionerService.getPractitionerById(record.practitionerId);
    const treatment = treatmentService.getTreatmentById(record.treatmentId);
    
    return {
      ...record,
      customerDetails: customer ? {
        name: customer.name,
        id: customer.id
      } : null,
      practitionerDetails: practitioner ? {
        name: practitioner.name,
        id: practitioner.id
      } : null,
      treatmentDetails: treatment ? {
        name: treatment.name,
        id: treatment.id
      } : null
    };
  }

  /**
   * Get customer treatment history summary
   * @param {string} customerId - Customer ID
   * @returns {Object} Summary of customer's treatment history
   */
  getCustomerTreatmentSummary(customerId) {
    const records = this.getTreatmentRecordsByCustomer(customerId);
    
    if (records.length === 0) {
      return {
        customerId,
        totalSessions: 0,
        firstVisitDate: null,
        lastVisitDate: null,
        mostCommonTreatments: [],
        mostCommonPractitioners: [],
        progressNotes: []
      };
    }
    
    // Sort records by date
    records.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Count treatments and practitioners
    const treatmentCounts = {};
    const practitionerCounts = {};
    
    records.forEach(record => {
      // Count treatments
      if (record.treatmentId) {
        treatmentCounts[record.treatmentId] = (treatmentCounts[record.treatmentId] || 0) + 1;
      }
      
      // Count practitioners
      if (record.practitionerId) {
        practitionerCounts[record.practitionerId] = (practitionerCounts[record.practitionerId] || 0) + 1;
      }
    });
    
    // Get most common treatments and practitioners
    const sortedTreatments = Object.entries(treatmentCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, count]) => {
        const treatment = treatmentService.getTreatmentById(id);
        return {
          id,
          name: treatment ? treatment.name : 'Unknown',
          count
        };
      });
    
    const sortedPractitioners = Object.entries(practitionerCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, count]) => {
        const practitioner = practitionerService.getPractitionerById(id);
        return {
          id,
          name: practitioner ? practitioner.name : 'Unknown',
          count
        };
      });
    
    // Compile progress notes from recent records
    const progressNotes = records.slice(-5).map(record => ({
      date: record.date,
      chiefComplaint: record.chiefComplaint,
      diagnosis: record.diagnosis,
      id: record.id
    }));
    
    return {
      customerId,
      totalSessions: records.length,
      firstVisitDate: records[0].date,
      lastVisitDate: records[records.length - 1].date,
      mostCommonTreatments: sortedTreatments,
      mostCommonPractitioners: sortedPractitioners,
      progressNotes
    };
  }

  /**
   * Get total number of treatment records
   * @returns {number} Record count
   */
  getTreatmentRecordCount() {
    return this.storageService.count();
  }

  /**
   * Import treatment records from JSON
   * @param {string} jsonData - JSON string of treatment record data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importTreatmentRecords(jsonData, merge = false) {
    return this.storageService.importData(jsonData, merge);
  }

  /**
   * Export treatment records as JSON
   * @returns {string} JSON string of treatment record data
   */
  exportTreatmentRecords() {
    return this.storageService.exportData();
  }
}

// Create singleton instance
const treatmentRecordService = new TreatmentRecordService();

export default treatmentRecordService;